import { InventoryItem, Currency, SalesTransaction } from '../types';
import { formatCurrency, getCurrentExchangeRate, convertUsdToTry, convertTryToUsd } from '../services/exchangeRateService';

/**
 * Calculates the cost of an item in TRY, considering exchange rates
 * If cost was in USD, uses the stored exchange rate
 */
export const getCostInTry = (item: InventoryItem): number => {
  if (item.costCurrency === Currency.TRY) {
    return item.costPerUnit;
  } else if (item.costExchangeRateToTry) {
    return convertUsdToTry(item.costPerUnit, item.costExchangeRateToTry);
  }
  return 0; // If no exchange rate is available
};

/**
 * Calculates the selling price of an item in TRY, considering exchange rates
 * For sold items, uses the exchange rate at time of sale (if available)
 * For unsold items, uses the current exchange rate
 */
export const getSellPriceInTry = async (item: InventoryItem): Promise<number> => {
  if (item.sellCurrency === Currency.TRY) {
    return item.sellPerUnit;
  }
  
  // For sold items with USD price, use the exchange rate at time of sale
  if (item.status === 'sold' && item.soldExchangeRateToTry) {
    return convertUsdToTry(item.sellPerUnit, item.soldExchangeRateToTry);
  }
  
  // For unsold items or sold items without stored rate, use current exchange rate
  const currentRate = await getCurrentExchangeRate();
  return convertUsdToTry(item.sellPerUnit, currentRate);
};

/**
 * Calculates the profit of an item in TRY
 */
export const getProfitInTry = async (item: InventoryItem): Promise<number> => {
  const costInTry = getCostInTry(item);
  const sellPriceInTry = await getSellPriceInTry(item);
  return sellPriceInTry - costInTry;
};

/**
 * Calculates the profit of an item in USD
 * This normalizes profit to a stable currency (USD)
 */
export const getProfitInUsd = async (item: InventoryItem): Promise<number> => {
  // If both cost and sell price are in USD, calculate profit directly
  if (item.costCurrency === Currency.USD && item.sellCurrency === Currency.USD) {
    return item.sellPerUnit - item.costPerUnit;
  }
  
  // Calculate profit in TRY then convert to USD using appropriate rate
  const profitInTry = await getProfitInTry(item);
  
  // For sold items, use exchange rate at time of sale if available
  if (item.status === 'sold' && item.soldExchangeRateToTry) {
    return convertTryToUsd(profitInTry, item.soldExchangeRateToTry);
  }
  
  // Otherwise use current exchange rate
  const currentRate = await getCurrentExchangeRate();
  return convertTryToUsd(profitInTry, currentRate);
};

/**
 * Gets profit margin as a percentage
 */
export const getProfitMargin = async (item: InventoryItem): Promise<number> => {
  const costInTry = getCostInTry(item);
  
  if (costInTry <= 0) return 0;
  
  const profitInTry = await getProfitInTry(item);
  return (profitInTry / costInTry) * 100;
};

/**
 * Formats an item's price in its original currency with appropriate symbol
 */
export const formatItemPrice = (item: InventoryItem, field: 'cost' | 'sell'): string => {
  const amount = field === 'cost' ? item.costPerUnit : item.sellPerUnit;
  const currency = field === 'cost' ? item.costCurrency : item.sellCurrency;
  
  return formatCurrency(amount, currency);
}; 

/**
 * Calculates the profit of a sales transaction in TRY.
 * Uses exchange rates stored within the transaction itself for accuracy.
 */
export const calculateTransactionProfitInTry = (transaction: SalesTransaction): number => {
  let sellingPriceInTry: number;
  let costPriceInTry: number;

  // Calculate selling price in TRY
  if (transaction.sellingCurrency === Currency.TRY) {
    sellingPriceInTry = transaction.sellingPrice;
  } else if (transaction.sellingCurrency === Currency.USD && transaction.sellingExchangeRateToTry) {
    sellingPriceInTry = convertUsdToTry(transaction.sellingPrice, transaction.sellingExchangeRateToTry);
  } else {
    // Fallback or error if USD sale is missing exchange rate (should not happen with current logic)
    console.warn(`Transaction ${transaction.id} (item: ${transaction.itemId}) is in USD but missing sellingExchangeRateToTry.`);
    sellingPriceInTry = convertUsdToTry(transaction.sellingPrice, 1); // Or handle as an error, or use a default rate
  }

  // Calculate cost price in TRY
  if (transaction.costCurrency === Currency.TRY) {
    costPriceInTry = transaction.costPrice;
  } else if (transaction.costCurrency === Currency.USD && transaction.costExchangeRateToTry) {
    costPriceInTry = convertUsdToTry(transaction.costPrice, transaction.costExchangeRateToTry);
  } else if (transaction.costCurrency === Currency.USD && !transaction.costExchangeRateToTry) {
    // Fallback or error if USD cost is missing exchange rate
    console.warn(`Transaction ${transaction.id} (item: ${transaction.itemId}) has USD cost but missing costExchangeRateToTry.`);
    costPriceInTry = convertUsdToTry(transaction.costPrice, 1); // Or handle as an error
  } else {
      costPriceInTry = transaction.costPrice; // Assume TRY if currency is not USD and no rate (should be TRY already)
  }
  
  return sellingPriceInTry - costPriceInTry;
};